import React, {useRef, useEffect, useState, useCallback, useMemo} from "react";
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, Title, Tooltip} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import './style.css';

ChartJS.register(Tooltip, Title, annotationPlugin);

const CV2ProcyonChart = ({ chartData, labelsChartData, titleBenchmark, clickedLegendIndex, displayOption }) => {
  const arrayColors = useMemo(() => { return ["#5b0428", "#122c49"]; }, []);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 991);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [optionDisplaySelected, setOptionDisplaySelected] = useState(displayOption)

  const lighten = (color, lighten)=>{
    const c = color.replace("#", "");
    let rgb = [parseInt(c.substr(0, 2), 16), parseInt(c.substr(2, 2), 16), parseInt(c.substr(4, 2), 16)];
    let returnstatement = "#";
    rgb.forEach((color)=>{returnstatement+=Math.round(((255-color)*(1-Math.pow(Math.E, -lighten))+color)).toString(16)});
    return returnstatement;
  }

  const buildDataSets = (chartData) => {
    let datasetsContainer = [];
    chartData.forEach(function (item, index) {
        datasetsContainer.push({
            label: item.type,
            fill: false,
            lineTension: 0.5,
            borderColor: "black",
            backgroundColor: buildArrayColors(index)[0].hover,
            borderWidth: 0,
            data: item.data,
            hoverBackgroundColor: buildArrayColors(index)[0].normal
        })
    });
    return datasetsContainer
  }

  const setThresholds = (chartData) => {
    // console.log(chartData);
    const thresholds = [];
    for (var i=0; i<chartData.length; i++) {
      if (chartData[i].threshold) {
        thresholds.push(chartData[i].threshold)
      }
    }

    if (thresholds.length === 0) return {};
    // console.log(thresholds);

    const thresholdLines = {};
    const positions = ['center', 'start', 'end'];
    for (var y=0; y < thresholds.length; y++) {
      thresholdLines['line' + (y+1)] = {
        type: 'line',
          xMin: thresholds[y],
          xMax: thresholds[y],
          borderColor: 'gray',
          borderWidth: 2,
          label: { content: chartData[y].type + ' threshold: ' + thresholds[y], display: true, position: positions[y] }
      }
    }
    // console.log(thresholdLines);
    return thresholdLines;
  }

  const buildArrayColors = useCallback((indexColor) => {
    const arrayColorsFinal = [{normal:[], hover: []}];
    for (let i=0; i<=chartData[0].data.length; i++ ) {
        arrayColorsFinal[0].hover.push(arrayColors[indexColor]);
        arrayColorsFinal[0].normal.push(lighten(arrayColors[indexColor], 1.2))
    }

    return arrayColorsFinal;
  }, [chartData, arrayColors]);
 
  const buildInfoByTable = (chartData, titleBenchmark, labelsChartData) => {
    //console.log(labelsChartData);
    let arrayFinal = [];
    let arrayMetrics = [];

    for (var i=0; i<labelsChartData.length; i++) {
      arrayFinal.push({"name": labelsChartData[i][0], "results": []}) 
    }

    chartData.forEach((info) => {
      for (var y=0; y<info.data.length; y++) {
        arrayFinal[y].results.push({"type": info.type, "value": info.data[y]})
      }
    })
    
    arrayFinal[0].results.forEach((item) => {
        const indexKey = arrayMetrics.findIndex(e => e.type === item.type);
        if (indexKey < 0) {
          arrayMetrics.push({"type": item.type, "metrics": []})
        }
    })

    return (
      <div className="containerTable">
           <div className="containerTableTable">
              <div className="legendTop">
                <div className="engine w-70">AI Inference Engine and device</div>
                <div className="metrics w-30">
                  {
                    arrayMetrics.map((info, index) => {
                        const classWidth = 'metric w-' + arrayMetrics.length;
                        const isDeeplabV3 = titleBenchmark === 'Deeplab V3 scores' ? true : false;
                        let newMeasureName = info.type;
                        let newMeasureName1 = ''
                        let newMeasureName2 = '';
                        if (isDeeplabV3) {
                          let lastIndex = info.type.lastIndexOf(' ');
                          newMeasureName1 = info.type.substr(0, lastIndex)
                          newMeasureName2 = info.type.substr(lastIndex + 1);
                        }
                        return (
                          <div key={index} className={classWidth}>
                            <div className="metricType">{isDeeplabV3 && (<div><div>{newMeasureName1}</div><div>{newMeasureName2}</div></div>)}{!isDeeplabV3 && (<div>{newMeasureName}</div>)}</div>
                            {
                              info.metrics.map((value, index) => {
                                const classMetricSubname = 'metricSubname w-'+ info.metrics.length;
                                return (<div key={index} className={classMetricSubname}>{value}</div>);
                              })
                            }
                          </div>
                        )
                    }
                  )
                  }
                </div>
              </div>
              <div className="contentResults">
                {
                  arrayFinal.map((measure, index) => {
                    return (
                      <div key={index} className="lineResult">
                        <div className="engine vertical-align-middle w-70">{measure.name}</div>
                        <div className="metrics vertical-align-middle w-30">
                          {
                            measure.results.map((item, index) => {
                              const classMetricValue = 'metricSubname vertical-align-middle + w-' + measure.results.length;
                              return (<div key={index} className={classMetricValue}><span className="mobileMetricTitle">{item.type}</span>{item.value}</div>);
                            })
                          }
                        </div>
                      </div>
                    )
                  })
                }
              </div>
           </div>
        </div>
    )
  }

  const data = {
    labels: labelsChartData,
    datasets: buildDataSets(chartData)
  };

  const options = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: {
      annotation: {
        annotations: setThresholds(chartData)
      },
      tooltip: {
        enabled: true,
        callbacks: {
            title: function(tooltipItems) {
                return '';
            },
            label: function(tooltipItem) {
                let secondString = 'Value: ' + tooltipItem.formattedValue;
                return [tooltipItem.dataset.label, secondString]
            }
        }
      },
      legend: {
        position: "top",
      },
      title: {
        display: true
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          // text: 'Accuracy',
          font: {
            weight: 'bold',
            size: '16px'
          },
        },
        offset: true,
        ticks: {
          font: {
            weight: 'bold',
            size: 12
          },
        },
        grid: {
          display: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'AI Inference engines',
          font: {
             weight: 'bold',
             size: '16px'
          }
        },
        beginAtZero: true,
        ticks: {
            crossAlign: 'center',
            autoSkip: false, 
            padding: 10,
            font: function(context) {
              // Calcola il font in base alla larghezza del canvas del grafico
              const width = context.chart.width;
              const size = width < 600 ? 10 : 12;
              const lineHeight = width < 600 ? 1.2 : 1;
              return {
                size: size,
                lineHeight: lineHeight
              };
            },
            callback: function(value) {
              const label = this.getLabelForValue(value)[0]; 
              const chartWidth = this.chart.width;
              const maxLength = Math.floor(chartWidth / 15); 

              if (label.length > maxLength) {
                let result = [];
                let words = label.split(' ');
                let line = '';

                words.forEach(word => {
                  if ((line + word).length <= maxLength) {
                    line += (line ? ' ' : '') + word;
                  } else {
                    result.push(line);
                    line = word;
                  }
                });
                result.push(line);
                return result;
              }
              return label;
            }
        },
        grid: {
          display: false
        },
      },
    },
    onClick: (e, element, chart) => {
      const datasetIndex = element[0]?.datasetIndex;

      if (datasetIndex === undefined) { // if click outside the chart bar, return
        return;
      }

      for (var i=0; i<chart.data.datasets.length; i++) {
        chart.data.datasets[i].backgroundColor = buildArrayColors(i)[0].normal // reset all chart with default color
      }
      chart.data.datasets[datasetIndex].backgroundColor = buildArrayColors(datasetIndex)[0].hover // set highlight color
      chart.update()
    },
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
    }
  };

  const chartRef = useRef(null);
  
  // going to check when tooltipChartData change
  useEffect(() => {
    const chartRefRef = chartRef.current;

    if (chartRefRef && clickedLegendIndex !== null) {

      for (var i=0; i<chartRefRef.data.datasets.length; i++) {
        chartRefRef.data.datasets[i].backgroundColor = buildArrayColors(i)[0].normal // reset all chart with default color
      }
      chartRefRef.data.datasets[clickedLegendIndex].backgroundColor = buildArrayColors(clickedLegendIndex)[0].hover // set highlight color

      chartRefRef.update()

    } else {

    }

  }, [clickedLegendIndex, buildArrayColors]);

  useEffect(() => { // check mobile and desktop version. Set mobile version as 991px as chakra UI style
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 991);
      setIsMobile(window.innerWidth <= 991)
      const chartRefRef = chartRef.current;
      if (chartRefRef) {

        for (var i=0; i<chartRefRef.data.datasets.length; i++) {
          chartRefRef.data.datasets[i].backgroundColor = buildArrayColors(i)[0].hover // reset all chart with default color
        }
        
        if (clickedLegendIndex !== null) {
          chartRefRef.data.datasets[clickedLegendIndex].backgroundColor = buildArrayColors(clickedLegendIndex)[0].normal // set highlight color
        }
        
        chartRefRef.update()
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clickedLegendIndex, buildArrayColors]);


  useEffect(() => {
    setOptionDisplaySelected(displayOption)
  }, [displayOption]);

  useEffect(() => {
    const chartRefRef = chartRef.current;

    if (chartRefRef && clickedLegendIndex !== null) {
      for (var i=0; i<chartRefRef.data.datasets.length; i++) {
        chartRefRef.data.datasets[i].backgroundColor = buildArrayColors(i)[0].normal // reset all chart with default color
      }
      chartRefRef.data.datasets[clickedLegendIndex].backgroundColor = buildArrayColors(clickedLegendIndex)[0].hover // set highlight color

      chartRefRef.update()

    } else {

    }

  }, [clickedLegendIndex, buildArrayColors]);
  
  return(
    (( ((isMobile) || ( isDesktop && optionDisplaySelected === 'chart' )) && <Bar id='myChartCanvas' ref={chartRef} data={data} options={options} />)
    || ((isDesktop && optionDisplaySelected !== 'chart') && buildInfoByTable(chartData, titleBenchmark, labelsChartData)))
  );
};

CV2ProcyonChart.propTypes = {
  chartData: PropTypes.array.isRequired
};

export default CV2ProcyonChart;
